import { logGreyError } from "owa-analytics";
import type { MailboxInfo } from "owa-client-types";
import type { CategoryActionSource } from "./CategoryActionSource";

export const CategoryActionOperation = {
    Add: "Add",
    Remove: "Remove",
} as const;

export type CategoryActionOperation = (typeof CategoryActionOperation)[keyof typeof CategoryActionOperation];

export const CategoryActionNoOpReason = {
    MissingTableView: "MissingTableView",
    MissingTargetId: "MissingTargetId",
    RowKeysNotResolved: "RowKeysNotResolved",
    ItemNotInStore: "ItemNotInStore",
    ItemMissingServerId: "ItemMissingServerId",
    ConversationContextNotResolved: "ConversationContextNotResolved",
} as const;

export type CategoryActionNoOpReason = (typeof CategoryActionNoOpReason)[keyof typeof CategoryActionNoOpReason];

export const CategoryActionTargetType = {
    Item: "Item",
    Items: "Items",
    Conversation: "Conversation",
    Conversations: "Conversations",
} as const;

export type CategoryActionTargetType = (typeof CategoryActionTargetType)[keyof typeof CategoryActionTargetType];

export interface CategoryActionNoOpContext {
    actionSource?: CategoryActionSource;
    reason: CategoryActionNoOpReason;
    targetType: CategoryActionTargetType;
    requestedTargetCount?: number;
    resolvedTargetCount: number;
    listViewType?: string | number;
    hasEffectiveTableView: boolean;
    hasItemId: boolean;
    isItemInStore?: boolean;
}

export interface CategoryActionNoTargetContext extends Omit<CategoryActionNoOpContext, "reason" | "resolvedTargetCount"> {
    didAttemptTargetResolution: boolean;
}

export function logCategoryActionNoOp(operation: CategoryActionOperation, mailboxInfo: MailboxInfo | undefined, context: CategoryActionNoOpContext): void {
    const customData = {
        ActionSource: context.actionSource,
        Reason: context.reason,
        TargetType: context.targetType,
        RequestedTargetCount: context.requestedTargetCount,
        ResolvedTargetCount: context.resolvedTargetCount,
        ListViewType: context.listViewType,
        HasEffectiveTableView: context.hasEffectiveTableView,
        HasItemId: context.hasItemId,
        IsItemInStore: context.isItemInStore,
    };
    
    if (operation === CategoryActionOperation.Add) {
        logGreyError("TnS_AddCategoryNoOp", mailboxInfo, undefined, customData);
    }
    else {
        logGreyError("TnS_RemoveCategoryNoOp", mailboxInfo, undefined, customData);
    }
}

export function logCategoryActionNoTarget(operation: CategoryActionOperation, mailboxInfo: MailboxInfo | undefined, context: CategoryActionNoTargetContext): void {
    const { didAttemptTargetResolution, ...noOpContext } = context;
    const reason = !context.hasEffectiveTableView
        ? CategoryActionNoOpReason.MissingTableView
        : didAttemptTargetResolution
            ? CategoryActionNoOpReason.RowKeysNotResolved
            : CategoryActionNoOpReason.MissingTargetId;
    
    logCategoryActionNoOp(operation, mailboxInfo, {
        ...noOpContext,
        reason,
        resolvedTargetCount: 0,
    });
}
